/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Typography, Box, Checkbox, Button, Grid } from '@mui/material';
import { loginRequest } from '../../auth/authConfig';
import { setLoadingState } from '../../actions/actions';
import { BackgroundCheckComponent, DisclosureComponent, ImageComponent, Header } from '..';
import { createPreference, fetchBackgroundCheck } from '../../common/services/Services';
import { getBanner } from '../../common/utilities/CustomBannerUtil';
import logo from './../../common/assets/img/agent-what-to-expect.png';
import HandleFormUtil from '../../common/utilities/HandleFormUtil';
import { initiateRestrictedErrorBanner } from '../../common/utilities/CommonHelperUtil';
import '../../common/theme/UserFlowPage.scss';
import '../../common/theme/Legal.scss';
import { LoadingComponent } from '../../components';

const LegalFormsComponent = ({refreshToken, accountInfo, contactId, user, setLegalFlowComplete, currentStep, setCurrentStep, setGlobalBannerProps, setShowGlobalBanner, setShowMinifiedHeader}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const authRequest = { ...loginRequest }; 
  authRequest.extraQueryParameters.userData = queryParams.toString().replace('userdata=', '');
  const { loaderStyle } = HandleFormUtil(null, null, null);
  const [isChecked, setIsChecked] = useState(false);
  const [backgroundCheckQuestions, setBackgroundCheckQuestions] = useState([]);
  const [agreement, setAgreement] = useState(false);
  const [isDisclosureRequired, setDisclosureRequired] = useState(undefined);
  const ciamId = accountInfo.oid;
  const email = accountInfo.email;
  const name = accountInfo.name;
  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const openGlobalRestrictedBanner = (errorMsg) => {
    initiateRestrictedErrorBanner(setGlobalBannerProps, setShowGlobalBanner, setShowMinifiedHeader, errorMsg);
    dispatch(setLoadingState(false));
  }

  const getBackgroundCheckData = async (token) => {
    if(user) {
      try {
        const data = await fetchBackgroundCheck(user, name, email, token);
        handleBackgroundCheckLegal(data);
      } catch (error) {
        openGlobalRestrictedBanner(error);
        setLegalFlowComplete(true);
      }
    } else {
      setLegalFlowComplete(true)
    }
  }

  useEffect(() => {
    dispatch(setLoadingState(true));
    if(isDisclosureRequired) {
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }

    if (currentStep !== 1) {
      setAgreement(true);
      const fetchData = async () => {
         const { token } = await refreshToken();
        await getBackgroundCheckData(token);
      };
      fetchData();
    } 
    dispatch(setLoadingState(false));
  }, []);

  const handleAgreement = async () => {
    dispatch(setLoadingState(true));
     const { token } = await refreshToken();
    const preferenceData = {'ciamId': ciamId,'contactId': contactId ,'new_name':'TERMS_CONDITIONS','new_value':'ACCEPTED'};
    try {
      await createPreference(preferenceData, token);
    } catch (error) {
      openGlobalRestrictedBanner(error);
      setLegalFlowComplete(true);
    }
    setAgreement(true);
    await getBackgroundCheckData(token);
    dispatch(setLoadingState(false));
  };

  const handleBackgroundCheckLegal = (data) => {
    data = data !== null ? data : {};
    const status = data && data.status ? data.status : null;
    if (status === "AWAITING") {
      if (data.message === "DISCLOSURE_INCOMPLETE" || data.message === "DISCLOSURE_NEEDED") {
        setCurrentStep(3);
        setDisclosureRequired(true);
      } else {
        setGlobalBannerProps(getBanner('subPendingBanner'));
        setShowGlobalBanner(true);
        setShowMinifiedHeader(true);
        setLegalFlowComplete(true);
      }
    } else if (status === "NOT_SIGNED") {
      setCurrentStep(2);
      if(data.data?.questions) {
        setBackgroundCheckQuestions(data.data?.questions);
      }
    } else if (status === "SIGNED" || status === "NOT_APPLICABLE") {
      setLegalFlowComplete(true);
    } else {
      throw new Error('Background check status not available. ');
    }
  };

  if ((currentStep === 2 && backgroundCheckQuestions.length === 0) || (currentStep === 3 && isDisclosureRequired !== true)) {
    return <LoadingComponent />;
  }  

  const shouldShowImageComponent = agreement && ((currentStep === 3 && isDisclosureRequired) || (backgroundCheckQuestions && backgroundCheckQuestions.length > 0));

  return (
    <>
      <Header />
      <div className='legal agent-ciam-container'>
        <div className='legal-container'>
          <Grid container justify='space-between' className='container-styling'>
              {shouldShowImageComponent && (
                <Grid size={{ xs: 12, lg: 5 }}>
                  <ImageComponent src={logo} />
                </Grid>
              )}
            <Grid size={{ xs: 12, lg: shouldShowImageComponent ? 7 : 12 }}>
              <Box className='legalTextContainer licensed-agent'>
                {currentStep === 3 && isDisclosureRequired && (
                  <DisclosureComponent name={name} email={email} user={user} refreshToken={refreshToken} openGlobalRestrictedBanner={openGlobalRestrictedBanner} />
                )}
                {backgroundCheckQuestions && backgroundCheckQuestions.length > 0 && (
                  <BackgroundCheckComponent 
                    setDisclosureRequired={setDisclosureRequired} 
                    setCurrentStep={setCurrentStep} 
                    handleBackgroundCheckLegal={handleBackgroundCheckLegal} 
                    setLegalFlowComplete={setLegalFlowComplete}
                    refreshToken={refreshToken} 
                    name={name} 
                    email={email} 
                    user={user} 
                    backgroundCheckQuestions={backgroundCheckQuestions}
                    openGlobalRestrictedBanner={openGlobalRestrictedBanner}/>
                )}
                {currentStep === 1 && agreement === false && (
                  <>
                    <Typography className='eyebrow'>American Modern | Systems Access Agreement</Typography>
                    <Typography className='title'>Systems Access Agreement</Typography>
                    <Typography className='title-text' gutterBottom>Please scroll, read and agree to the terms and conditions to proceed.</Typography>
                    <Typography>American Modern Insurance Group, Inc. (the "Company") provides and delivers web-based applications in connection with its activities (the "System"). This Agreement sets forth the terms and conditions for your access to and use of the System.</Typography>
                    <Typography>BY CLICKING THE "ACCEPT" BUTTON AT THE END OF THIS AGREEMENT, YOU REPRESENT THAT YOU: (1) HAVE READ AND UNDERSTOOD THIS AGREEMENT; AND (2) AGREE TO COMPLY WITH AND BE BOUND BY THE TERMS AND CONDITIONS OF THIS AGREEMENT. If you do not agree to the terms and conditions of this Agreement, you will not be permitted to access and use the System.</Typography>
                    <Typography className="subheader">Ownership</Typography>
                    <Typography>You acknowledge that the Company is and shall continue to be the absolute owner of all right, title, and interest in and to the System. </Typography>
                    <Typography className="subheader">Access</Typography>
                    <Typography>The Company may at any time, with or without notice, restrict the use and accessibility of the System. The Company may also temporarily suspend or revoke your rights to use the System based on the Company's belief or suspicion that your use of the System violates this Agreement or any applicable law.</Typography>
                    <Typography className="subheader">Password</Typography>
                    <Typography>You are responsible for all acts or omissions that occur as a result of the use of your user identification name ("User Name") and password. You are responsible for protecting and securing your User Name and password from unauthorized access, use, or disclosure. You must commit your password to memory. Your User Name and password are for your use alone and you shall not disclose your User Name or password to anyone or allow anyone to access the System using your User Name and password. You shall change your password at least every 60 days, and immediately upon learning or having reason to know that your password has been obtained or accessed by someone else. Your password must be at least 7 characters and may not be found in any dictionary.</Typography>
                    <Typography className="subheader">Restricted Activities</Typography>
                    <Typography>In connection with your use of the System, you shall not: (1) copy, reproduce, modify, translate, reverse engineer, decompile, or disassemble the System, nor permit anyone else to do so; (2) violate any law, statute, ordinance or regulation; (3) interfere or attempt to interfere with the proper working of the System; or (4) distribute or publicly display the System or any information contained on the System unless in the normal course of conducting business on behalf of the Company. You shall not place anything on the site that may contain any viruses or other computer programming routines which may damage or interfere with the System.</Typography>
                    <Typography className="subheader">Non-Disclosure</Typography>
                    <Typography>In conjunction with your use of the System, you may obtain or gain access to confidential information related to the System, the Company, and the Company's customers. You shall not directly or indirectly disclose any such information or use it for any purpose other than the performance of your responsibilities on behalf of the Company. You acknowledge that the information contained on the System is subject to privacy rules, including requirements of the Gramm-Leach-Bliley Act.</Typography>
                    <Typography className="subheader">DISCLAIMER OF WARRANTIES</Typography>
                    <Typography>There may be delays, omissions or inaccuracies in the System and the information and services available through the System. You understand and agree that your use of the System is at your sole risk. The Company does not represent or warrant that the System will be uninterrupted or error free. The Company does not make any representation or warranty as to the timeliness, sequence, accuracy, completeness, reliability or content of any information, service or transaction in connection with the System. You agree that the System and all data and information on the System are furnished on an "as is" basis. ALL WARRANTIES, EXPRESS OR IMPLIED, ARE HEREBY DISCLAIMED AND EXCLUDED. THIS DISCLAIMER SPECIFICALLY INCLUDES ANY IMPLIED WARRANTIES OF MERCHANTIBILITY OR FITNESS FOR A PARTICULAR PURPOSE.</Typography>
                    <Typography className="subheader">LIMITATION ON LIABILITY</Typography>
                    <Typography>IN USING THE SYSTEM, YOU AGREE THAT THE COMPANY SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL, EXEMPLARY, PUNITIVE OR CONSEQUENTIAL DAMAGES OR PENALTIES OF ANY KIND ARISING OUT OF MY USE OF THE SYSTEM, EVEN IF THE COMPANY HAS BEEN ADVISED OF THE POSSIBILITY THEREOF.</Typography>
                    <Typography className="subheader">Modification</Typography>
                    <Typography>The Company may modify the terms and conditions of this Agreement at any time and from time to time. Upon modification of such terms and conditions, I understand that, in order to continue to access and use the System, I will be required to reaffirm my consent to this Agreement.</Typography>
                    <Box className='agreementBox' display='flex' alignItems='center'>
                      <Checkbox className='agreementCheckbox' disableRipple checked={isChecked} onChange={handleCheckboxChange} />
                      <Typography className='agreementText'>By checking here as my electronic signature, I agree to be bound by the terms and conditions of this agreement. 
                          (If you do not agree to the terms and conditions of this agreement, you will not be granted access to the system.)</Typography>
                    </Box>
                    <Box className='continueButton'>
                      <Button
                        disableRipple
                        onClick={handleAgreement}
                        className='button-styling continue-button'
                        style={loaderStyle}
                        disabled={!isChecked}
                      >
                        Agree
                  </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </div>
      </div>
    </>
  );
};

export default LegalFormsComponent;
